<template>
  <div v-if="spatialFact" class="evidence-box evidence-spatial mb-3 pt-3 border-t-subtle">
    <div class="evidence-box-title d-flex align-center mb-1">
      <v-icon size="16" color="#2563eb" class="mr-1">mdi-lan-connect</v-icon>
      <span class="section-sub-title">同站设备关联状态排查</span>
    </div>

    <p class="evidence-box-desc pl-5">
      {{ spatialFact }}
    </p>

    <!-- 关联离线设备标签列表 -->
    <div v-if="concurrentDevicesFact" class="concurrent-devices-row mt-2 pl-5">
      <span class="concurrent-label text-caption text-slate-500">同一时段发生告警/离线的其他设备：</span>
      <div class="concurrent-tags">
        <span
          v-for="devName in concurrentDevicesFact.split('、')"
          :key="devName"
          class="concurrent-tag"
        >
          <v-icon size="12" class="mr-1">mdi-server-network</v-icon>
          {{ devName }}
        </span>
      </div>
    </div>

    <!-- 空间拓扑明细表格（默认合住，可点击展开查看） -->
    <div class="embedded-table-section mt-3 ml-5">
      <div class="table-toggle-bar" @click="isOpen = !isOpen">
        <div class="toggle-bar-left">
          <v-icon size="14" color="#2563eb" class="mr-1">
            {{ isOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
          </v-icon>
          <span class="toggle-title">本电站同时段异常告警明细 (共 {{ alarmsList.length }} 条)</span>
        </div>
        <span class="toggle-btn-label">
          {{ isOpen ? '收起表格' : '展开查看明细' }}
        </span>
      </div>

      <div v-show="isOpen" class="table-content-panel">
        <div v-if="alarmsList.length" class="table-scroll-wrap">
          <table class="modern-mini-table">
            <thead>
              <tr>
                <th style="width: 42px;">#</th>
                <th>设备名称</th>
                <th>设备编码 (UUID)</th>
                <th>告警描述</th>
                <th>告警码</th>
                <th>发生时间</th>
                <th style="width: 86px;">状态</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in alarmsList"
                :key="idx"
                :class="{ 'highlight-target-row': isTargetDevice(row) }"
              >
                <td>{{ idx + 1 }}</td>
                <td>
                  <div class="dev-name-cell">
                    <span class="font-weight-medium">{{ row.equipmentName || '--' }}</span>
                    <span v-if="isTargetDevice(row)" class="target-badge">本设备</span>
                  </div>
                </td>
                <td class="code-font">{{ row.uuid || '--' }}</td>
                <td>{{ row.description || '--' }}</td>
                <td class="code-font">{{ row.alarmCode || row.code || '--' }}</td>
                <td>{{ row.startTime ? row.startTime.slice(0, 19) : '--' }}</td>
                <td>
                  <span v-if="row.endTime && row.endTime !== '/' && row.endTime !== '--'" class="status-pill pill-recovered">已恢复</span>
                  <span v-else class="status-pill pill-active">未恢复</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="table-empty-wrap">
          <v-icon size="15" color="#bfbfbf" class="mr-1">mdi-information-outline</v-icon>
          <span>当前时段该电站未查询到其他告警记录（仅当前设备单机离线）</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const props = defineProps({
  spatialFact: {
    type: String,
    default: ''
  },
  concurrentDevicesFact: {
    type: String,
    default: ''
  },
  alarmsList: {
    type: Array,
    default: () => []
  },
  alarmRecord: {
    type: Object,
    default: () => ({})
  }
})

const isOpen = ref(false) // 默认合住

function isTargetDevice(row) {
  if (!row) return false
  const targetUuid = String(props.alarmRecord?.uuid || props.alarmRecord?.deviceVid || props.alarmRecord?.equipmentUUID || '').trim()
  const targetName = String(props.alarmRecord?.equipmentName || props.alarmRecord?.deviceName || '').trim()
  const rowUuid = String(row.uuid || row.deviceVid || row.equipmentUUID || '').trim()
  const rowName = String(row.equipmentName || row.deviceName || '').trim()
  return (targetUuid && rowUuid && targetUuid === rowUuid) || (targetName && rowName && targetName === rowName)
}
</script>

<style scoped lang="scss">
.evidence-box {
  border-radius: 10px;
  padding: 14px 16px;
  border: 1px solid #f1f5f9;
  background: #f8fafc;

  .evidence-box-title {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;

    .evidence-status-tag {
      font-size: 10px;
      padding: 1px 6px;
      border-radius: 4px;
      margin-left: 6px;
      font-weight: 600;

      &.tag-spatial {
        background: #eff6ff;
        color: #2563eb;
      }
    }
  }

  .section-sub-title {
    font-size: 14px !important;
    font-weight: 700 !important;
    color: #1e293b !important;
    line-height: 1.4;
  }

  .evidence-box-desc {
    font-size: 14px;
    line-height: 1.65;
    color: #334155;
    margin: 0;
  }

  &.evidence-spatial {
    background: transparent;
    border: none;
    padding: 0;
  }

  .concurrent-devices-row {
    display: flex;
    flex-direction: column;
    gap: 6px;

    .concurrent-label {
      font-size: 12px;
      color: #64748b;
    }

    .concurrent-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;

      .concurrent-tag {
        display: inline-flex;
        align-items: center;
        background: rgba(58, 104, 255, 0.04);
        border: 1px solid rgba(58, 104, 255, 0.15);
        color: #2563eb;
        padding: 2px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
      }
    }
  }

  .embedded-table-section {
    margin-top: 14px;
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
    overflow: hidden;

    .table-toggle-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 16px;
      background: #f8fafc;
      cursor: pointer;
      user-select: none;
      transition: all 0.2s ease;
      border-bottom: 1px solid transparent;

      &:hover {
        background: #f1f5f9;
      }

      .toggle-bar-left {
        display: flex;
        align-items: center;

        .toggle-title {
          font-size: 13px;
          font-weight: 600;
          color: #1e293b;
        }
      }

      .toggle-btn-label {
        font-size: 12px;
        color: #2655f1;
        font-weight: 500;
        transition: opacity 0.2s;

        &:hover {
          opacity: 0.85;
        }
      }
    }

    .table-content-panel {
      border-top: 1px solid #e2e8f0;
      padding: 0;

      .table-scroll-wrap {
        max-height: 300px;
        overflow-y: auto;
        overflow-x: auto;

        &::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        &::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        &::-webkit-scrollbar-track {
          background: transparent;
        }
      }

      .modern-mini-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
        line-height: 1.5;

        th {
          position: sticky;
          top: 0;
          background: #f8fafc;
          color: #475569;
          font-weight: 600;
          font-size: 12px;
          padding: 6px 10px;
          text-align: left;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap;
          z-index: 1;
        }

        td {
          padding: 6px 10px;
          color: #1e293b;
          font-size: 12px;
          border-bottom: 1px solid #f1f5f9;
          vertical-align: middle;

          &.code-font {
            font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
            color: #475569;
            font-size: 11.5px;
          }
        }

        tr:hover td {
          background: #f8faff !important;
        }

        tr.highlight-target-row td {
          background: #eff6ff !important;
          font-weight: 500;
        }

        .dev-name-cell {
          display: flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;

          .target-badge {
            display: inline-flex;
            align-items: center;
            font-size: 10px;
            color: #2655f1;
            background: #dbeafe;
            border: 1px solid #bfdbfe;
            padding: 0 5px;
            border-radius: 3px;
            font-weight: 600;
            line-height: 16px;
          }
        }

        .status-pill {
          display: inline-block;
          padding: 1px 7px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 500;
          text-align: center;
          line-height: 16px;
          white-space: nowrap;

          &.pill-recovered {
            background: #f0fdf4;
            color: #16a34a;
            border: 1px solid #bbf7d0;
          }

          &.pill-active {
            background: #fef2f2;
            color: #dc2626;
            border: 1px solid #fecaca;
          }
        }
      }

      .table-empty-wrap {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px 16px;
        color: #64748b;
        font-size: 13px;
        background: #ffffff;
      }
    }
  }
}
</style>
